import { ArrayUtil, MenuItem, StringUtil } from "@mcleod/core";
import { RowWebMenuGroup, RowWebMenuGroupType } from "../models/ModelWebMenuGroup";
import { RowWebMenuItem } from "../models/ModelWebMenuItem";
import { RowWebMenuGroupFields } from "../models/autogen/AutogenModelWebMenuGroup";
import { RowWebMenuItemFields } from "../models/autogen/AutogenModelWebMenuItem";
import { MenuEditorUtil } from "./MenuEditorUtil";

export interface DatabaseMenuItemProps extends MenuItem {
    appending: boolean;

    /** The web_menu_item data for this menu item (caption, path, favorite_caption, etc.) */
    itemData: RowWebMenuItem | Partial<RowWebMenuItemFields>;

    /** web_menu_group record that serves as a link to the parent menu group, child menu groups, and web_menu_item.
     * For shared menus, this does not link to the parent menu group, only the children that make up the shared menu.
     *
     * web_menu_item provides the content, web_menu_group provides the menu structure. */
    groupData: RowWebMenuGroup | Partial<RowWebMenuGroupFields>;

    /** The reference to the shared web_menu_group. This is only set when including a shared menu.
     * This links the shared menu (include_group_id) to this menuItem's parent (parent_id) */
    groupReferenceData: RowWebMenuGroup | Partial<RowWebMenuGroupFields>;

    items?: DatabaseMenuItemProps[];
    isMainMenu?: boolean;
}

export class DatabaseMenuItem implements DatabaseMenuItemProps {
    image: string;
    private _appending: boolean;
    private _groupReferenceData: RowWebMenuGroup;
    private _itemData: RowWebMenuItem;
    private _groupData: RowWebMenuGroup;
    private _items: DatabaseMenuItem[];
    private _caption: string;
    private _path: string;
    private _favoriteCaption: string;
    private _parent: DatabaseMenuItem;
    private _isMainMenu: boolean;

    constructor(props?: Partial<DatabaseMenuItemProps>, appendingOverride?: boolean) {
        this._appending = appendingOverride ?? props?.appending ?? true;
        this.itemData = props?.itemData;
        this.groupData = props?.groupData;
        this.caption = props?.caption;
        this.path = props?.path;
        this.favoriteCaption = props?.favoriteCaption;
        this.groupReferenceData = props?.groupReferenceData;
        this.items = props?.items?.map(item => new DatabaseMenuItem(item, appendingOverride));
        this._isMainMenu = props?.isMainMenu ?? false;
    }

    get isLeaf(): boolean {
        return ArrayUtil.isEmptyArray(this.items);
    }

    get isMainMenu(): boolean {
        return this._isMainMenu && this.parent == null;
    }

    get isTopLevel(): boolean {
        return this.parent != null && this.parent.isMainMenu;
    }

    get itemData(): RowWebMenuItem {
        return this._itemData;
    }

    set itemData(value: RowWebMenuItem | Partial<RowWebMenuItemFields>) {
        this._itemData = MenuEditorUtil.resolveItemRow(value, this.appending);
    }

    get parent(): DatabaseMenuItem {
        return this._parent;
    }

    get groupReferenceData(): RowWebMenuGroup {
        return this._groupReferenceData;
    }

    set groupReferenceData(value: RowWebMenuGroup | Partial<RowWebMenuGroupFields>) {
        this._groupReferenceData = MenuEditorUtil.resolveGroupRow(value, this.appending);
    }

    get groupData(): RowWebMenuGroup {
        return this._groupData;
    }

    set groupData(value: RowWebMenuGroup | Partial<RowWebMenuGroupFields>) {
        this._groupData = MenuEditorUtil.resolveGroupRow(value, this.appending);
    }

    get caption(): string {
        return this.itemData.get("caption") ?? this._caption;
    }

    set caption(value: string) {
        this._caption = value;
    }

    get path(): string {
        return this.itemData.get("path") ?? this._path;
    }

    set path(value: string) {
        this._path = value;
    }

    get favoriteCaption(): string {
        return this.itemData.get("favorite_caption") ?? this._favoriteCaption;
    }

    set favoriteCaption(value: string) {
        this._favoriteCaption = value;
    }

    get appending(): boolean {
        return this._appending ?? true;
    }

    hasChanged(): boolean {
        return this.itemData.hasChanged() ||
            this.groupData.hasChanged() ||
            this.groupReferenceData?.hasChanged() ||
            this.items?.some(item => item.hasChanged());
    }

    get items() : DatabaseMenuItem[] {
        return this._items;
    }

    set items(items: DatabaseMenuItem[]) {
        this._items = null;
        if (!ArrayUtil.isEmptyArray(items)) {
            for (const item of items) {
                this.internalAddChild(item);
            }
        }
    }

    private internalAddChild(child: DatabaseMenuItem) {
        this._items ??= [];
        this._items.push(child);
        child._parent = this;
        return child;
    }

    public addChild(child: DatabaseMenuItem, updateGroupReference: boolean = false): DatabaseMenuItem {
        this.internalAddChild(child);
        this.sortItems();
        const groupId = this.groupData?.get("id");
        if (groupId == null) {
            throw new Error("Cannot add child to menu item with no group data");
        }
        // if we are moving a reference to a shared menu, we want to update the child.groupReferenceData
        const childGroupDataToUpdate = updateGroupReference? child.groupReferenceData : child.groupData;
        childGroupDataToUpdate.set("parent_id", groupId);
        return child;
    }

    sortItems() {
        if (this.isMainMenu) {
           this.sortTopLevelItems();
        } else {
            this.sortChildItems();
        }
    }

    private sortTopLevelItems() {
        this.items.sort((a, b) => {
            const getPosition = (item: DatabaseMenuItem) =>
                (item.groupReferenceData ?? item.groupData).get("item_position") ?? 0;
            return getPosition(a) - getPosition(b);
        });
    }

    private sortChildItems() {
        this.items.sort((a, b) => {
            if (a.isLeaf !== b.isLeaf) {
                return a.isLeaf ? 1 : -1;
            }
            return (a.caption ?? "").localeCompare(b.caption ?? "");
        });
    }

    public removeChildByGroupReferenceId(groupReferenceId: string) {
        const child = this.internalRemoveChild(item => item.groupReferenceData?.get("id") === groupReferenceId);
        if (child == null) {
            return;
        }
        this.nullifyParent(child);
    }

    private nullifyParent(item: DatabaseMenuItem) {
        item._parent = null;
        item.items?.forEach(childItem => this.nullifyParent(childItem));
    }

    public removeChild(child: DatabaseMenuItem) {
        this.internalRemoveChild(item => item === child);
    }

    private internalRemoveChild(filterFunction: (item: DatabaseMenuItem) => boolean): DatabaseMenuItem {
        if (!ArrayUtil.isEmptyArray(this._items)) {
            for (const item of this._items) {
                if (filterFunction(item)) {
                    this._items = this._items.filter(i => i !== item);
                    this.nullifyParent(item);
                    return item;
                }
            }
        }
        return null;
    }

    public update(webMenuItemValues: Partial<RowWebMenuItemFields>) : boolean {
        if (webMenuItemValues == null) {
            return false;
        }
        this.itemData.clear();
        this.itemData.setValues(webMenuItemValues);
        return true;
    }

    recurseItems(recurseFunction: (child: DatabaseMenuItem) => void) {
        for (const child of this.items ?? []) {
            recurseFunction(child);
            child.recurseItems(recurseFunction);
        }
    }

    getRecursiveItems(matchFunction?: (child: DatabaseMenuItem) => boolean): DatabaseMenuItem[] {
        const items: DatabaseMenuItem[] = [];
        this.recurseItems(child => {
            if (matchFunction == null || matchFunction(child))
                items.push(child);
        });
        return items;
    }

    findFirstItem(matchFunction: (menuItem: DatabaseMenuItem) => boolean): DatabaseMenuItem | null {
        for (const menuItem of this.items ?? []) {
            if (matchFunction(menuItem)) {
                return menuItem;
            }
            const found = menuItem.findFirstItem(matchFunction);
            if (found) {
                return found;
            }
        }
        return null;
    }

    public createCopy(): DatabaseMenuItem {
        const copy = new DatabaseMenuItem(this);
        copy.groupData._appending = this.groupData._appending;
        copy.itemData._appending = this.itemData._appending;
        if (copy.groupReferenceData != null) {
            copy.groupReferenceData._appending = this.groupReferenceData._appending;
        }
        return copy;
    }

    public get compositeKey(): string {
        return [
            this.groupData.get("id"),
            this.itemData.get("id"),
            this.groupReferenceData?.get("id")
        ].filter(id => !StringUtil.isEmptyString(id)).join("_");
    }

    public isSharedMenuReference(): boolean {
        return this.groupReferenceData != null;
    }

    public async convertToShared() {
        const sharedGroup = new RowWebMenuGroup();
        await sharedGroup.populateDefaultValues();
        sharedGroup.setValues({
            item_id: this.itemData.get("id"),
            group_type: RowWebMenuGroupType.SHARED
        })

        // current group now becomes a shared reference
        this.groupReferenceData = this.groupData;
        this.groupReferenceData.setValues({
            group_type: RowWebMenuGroupType.SHARED_REFERENCE,
            include_group_id: sharedGroup.get("id"),
            item_id: undefined
        });

        this.groupData = sharedGroup;
        this.groupData._appending = true;

        // point all child groups to new shared group
        for (const child of this.items) {
            child.groupData.set("parent_id", sharedGroup.get("id"));
        }
    }

    public convertFromShared(): RowWebMenuGroup {
        if (this.groupReferenceData == null) {
            return null;
        }
        this.groupData.setValues({
            group_type: RowWebMenuGroupType.BRANCH,
            parent_id: this.groupReferenceData.get("parent_id")
        });

        const result = this.groupReferenceData;
        this.groupReferenceData = null;
        return result;
    }

    public static isExternalPath(path: string): boolean {
        if (StringUtil.isEmptyString(path)) {
            return false;
        }
        const lowerPath = path.toLowerCase();
        return lowerPath.startsWith("https") || lowerPath == "url";
    }

    public static async createNewMenuItem(): Promise<DatabaseMenuItem> {
        const itemData = new RowWebMenuItem();
        await itemData.populateDefaultValues();
        const groupData = new RowWebMenuGroup();
        await groupData.populateDefaultValues();
        groupData.set("item_id", itemData.get("id"));
        return new DatabaseMenuItem({ itemData, groupData });
    }
}
